import { format } from "date-fns"
import { CalendarClock, FileCheck, Mail } from "lucide-react"

import type { FrontendTriggerDefinition } from "../../types"

export const systemTriggers: FrontendTriggerDefinition[] = [
  {
    key: "trigger__form_accepted",
    label: "Form accepted",
    description: "Triggers when a form submission is accepted into a household",
    icon: FileCheck,
    providesSubjects: ["household", "person"],
    inputFields: [
      { key: "intake_form_id", label: "Form", inputType: "select", required: false, options: "dynamic_intake_forms" },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "form_submission",
      },
    ],
  },
  {
    key: "trigger__email_received",
    label: "Email received",
    description: "Triggers when an email is received",
    icon: Mail,
    providesSubjects: ["email_message"],
    inputFields: [
      {
        key: "email_integration",
        label: "Email integration",
        inputType: "select",
        required: true,
        options: "dynamic_email_integrations",
      },
      {
        key: "matching_existing_contact",
        label: "Trigger for",
        inputType: "select",
        required: true,
        defaultValue: "false",
        options: [
          { value: "false", label: "Emails from unknown senders" },
          { value: "true", label: "Emails from existing contacts" },
        ],
      },
      {
        key: "filter_group",
        label: "Continue if...",
        inputType: "filter_group",
        required: false,
        filterEntity: "email",
      },
    ],
  },
  {
    key: "trigger__recurring",
    label: "Recurring",
    description: "Triggers on a weekly, monthly, or yearly schedule in your book's timezone",
    icon: CalendarClock,
    providesSubjectsByInputs: [
      {
        field: "report_record_type",
        when: { field: "run_scope", values: ["filter_report"] },
        values: {
          client: ["household"],
          prospect: ["household"],
          past_client: ["household"],
          person: ["person", "household"],
          contact: ["contact", "person"],
          meeting: ["meeting", "household"],
          task: ["task", "household"],
        },
      },
      {
        field: "report_view_type",
        when: { field: "run_scope", values: ["filter_report"] },
        values: {
          person: ["person"],
        },
      },
    ],
    inputFields: [
      {
        key: "interval_count",
        label: "Every",
        inputType: "number",
        required: true,
        defaultValue: 1,
      },
      {
        key: "frequency",
        label: "Period",
        inputType: "select",
        required: true,
        defaultValue: "monthly",
        options: [
          { value: "weekly", label: "week" },
          { value: "monthly", label: "month" },
          { value: "yearly", label: "year" },
        ],
      },
      {
        key: "month_of_year",
        label: "Month",
        inputType: "select",
        required: false,
        defaultValue: "1",
        options: [
          { value: "1", label: "January" },
          { value: "2", label: "February" },
          { value: "3", label: "March" },
          { value: "4", label: "April" },
          { value: "5", label: "May" },
          { value: "6", label: "June" },
          { value: "7", label: "July" },
          { value: "8", label: "August" },
          { value: "9", label: "September" },
          { value: "10", label: "October" },
          { value: "11", label: "November" },
          { value: "12", label: "December" },
        ],
        visibleWhen: { field: "frequency", values: ["yearly"] },
      },
      {
        key: "day_of_period",
        label: "Day",
        inputType: "select",
        required: false,
        defaultValue: "1",
        options: "dynamic_recurring_days",
        visibleWhen: { field: "frequency", values: ["weekly", "monthly", "yearly"] },
      },
      {
        key: "time_of_day",
        label: "At",
        inputType: "time",
        required: true,
        defaultValue: "08:00",
      },
      {
        key: "starts_on",
        label: "Starting",
        inputType: "date",
        required: true,
        defaultValue: format(new Date(), "yyyy-MM-dd"),
      },
      {
        key: "run_scope",
        label: "Run for",
        inputType: "radio",
        required: true,
        defaultValue: "book",
        options: [
          {
            value: "book",
            label: "Once per book",
            description: "Start one automation run for the book on each scheduled date.",
          },
          {
            value: "filter_report",
            label: "Every record in a report",
            description: "Refresh the report at run time and start a separate run for each matching record.",
          },
        ],
      },
      {
        key: "report_id",
        label: "Report",
        inputType: "select",
        required: true,
        options: "dynamic_recurring_reports",
        visibleWhen: { field: "run_scope", values: ["filter_report"] },
      },
      {
        key: "report_record_type",
        label: "Report record type",
        inputType: "hidden",
        required: false,
      },
      {
        key: "report_view_type",
        label: "Report view type",
        inputType: "hidden",
        required: false,
      },
    ],
  },
]
